/**
 * Unified Navigation Configuration
 * LAYER-SHELL: Role-based navigation
 *
 * Single unified navigation experience. Users see ALL items their role can access.
 * No portal switching required - everything is in one sidebar.
 *
 * Role Access (strict portal separation; All Transactions is shared lookup):
 * - CUSTOMER: Customer-facing items only.
 * - CASE_MANAGER: CM dashboard + All Transactions.
 * - COMPLIANCE: Compliance dashboard + All Transactions.
 * - OPERATIONS: All Transactions + Reports + Notifications (read-only audit).
 *   Issue refunds and unlock users from the transaction detail page.
 *   Cannot access dashboards, user management, discounts, or notification edits.
 * - ADMIN / SUPER_ADMIN: Everything (admin tier inherits all staff dashboards).
 *
 * "All Transactions" is the shared lookup surface — every staff role needs it
 * to find a transaction by code/customer. Dashboards remain role-strict so
 * the role-management UI is predictable: granting CM grants CM dashboard
 * only, not Compliance dashboard.
 */

import type { UserRole } from '../components/Shell/Shell';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: string; // Heroicon component name
  roles: UserRole[];
  /** Optional section for visual grouping */
  section?: 'customer' | 'cases' | 'compliance' | 'admin';
}

/**
 * All navigation items.
 * Items are filtered by role at runtime.
 * Each user sees all items their role permits - no portal switching needed.
 */
export const navigationItems: NavItem[] = [
  // ========================
  // Customer Section
  // ========================
  {
    id: 'customer-overview',
    label: 'Home',
    path: '/accounts/me',
    icon: 'HomeIcon',
    roles: ['CUSTOMER'],
    section: 'customer',
  },
  {
    id: 'customer-account',
    label: 'Account',
    path: '/accounts/details',
    icon: 'UserCircleIcon',
    roles: ['CUSTOMER'],
    section: 'customer',
  },
  {
    id: 'customer-billing',
    label: 'Billing',
    path: '/accounts/billing',
    icon: 'CreditCardIcon',
    roles: ['CUSTOMER'],
    section: 'customer',
  },

  // ========================
  // Case Management Section
  // Strict: only CASE_MANAGER (and ADMIN/SUPER_ADMIN by hierarchy).
  // ========================
  {
    id: 'cm-dashboard',
    label: 'Case Manager',
    path: '/case-manager/dashboard',
    icon: 'Squares2X2Icon',
    roles: ['CASE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    section: 'cases',
  },
  // TODO: Add routes for these when implemented
  // {
  //   id: 'cm-respond',
  //   label: 'Respond',
  //   path: '/case-manager/respond',
  //   icon: 'ChatBubbleLeftRightIcon',
  //   roles: ['CASE_MANAGER', 'COMPLIANCE', 'ADMIN', 'SUPER_ADMIN'],
  //   section: 'cases',
  // },
  // {
  //   id: 'cm-cases',
  //   label: 'My Cases',
  //   path: '/case-manager/cases',
  //   icon: 'FolderIcon',
  //   roles: ['CASE_MANAGER', 'COMPLIANCE', 'ADMIN', 'SUPER_ADMIN'],
  //   section: 'cases',
  // },

  // ========================
  // Compliance Section
  // Strict: only COMPLIANCE (and ADMIN/SUPER_ADMIN by hierarchy).
  // ========================
  {
    id: 'compliance-dashboard',
    label: 'Compliance',
    path: '/compliance/dashboard',
    icon: 'ShieldCheckIcon',
    roles: ['COMPLIANCE', 'ADMIN', 'SUPER_ADMIN'],
    section: 'compliance',
  },
  // TODO: These are views within the compliance dashboard, not separate routes
  // {
  //   id: 'compliance-queue',
  //   label: 'Review Queue',
  //   path: '/compliance/queue',
  //   icon: 'QueueListIcon',
  //   roles: ['COMPLIANCE', 'CASE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
  //   section: 'compliance',
  // },
  // {
  //   id: 'compliance-review',
  //   label: 'Documents',
  //   path: '/compliance/documents',
  //   icon: 'DocumentMagnifyingGlassIcon',
  //   roles: ['COMPLIANCE', 'CASE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
  //   section: 'compliance',
  // },
  // {
  //   id: 'compliance-approvals',
  //   label: 'Approvals',
  //   path: '/compliance/approvals',
  //   icon: 'ShieldCheckIcon',
  //   roles: ['COMPLIANCE', 'CASE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
  //   section: 'compliance',
  // },

  // ========================
  // Admin Section
  // Admin tier only: Admin and Super Admin
  // ========================
  {
    id: 'admin-dashboard',
    label: 'Dashboard',
    path: '/admin/dashboard',
    icon: 'ChartBarSquareIcon',
    roles: ['ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    // Operations' primary surface — the post-compliance fulfillment
    // dashboard. Visible to OPERATIONS + ADMIN + SUPER_ADMIN. Pure CM
    // and pure COMPLIANCE intentionally do NOT see this — matches the
    // strict-portal-separation model. PR 3 of the Delivery rollout.
    id: 'operations-delivery',
    label: 'Delivery',
    path: '/admin/delivery',
    icon: 'TruckIcon',
    roles: ['OPERATIONS', 'ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    id: 'admin-transactions',
    label: 'All Transactions',
    // Shared lookup surface — every staff role needs this to find a
    // transaction by code/customer. Path lives under /admin/ for legacy
    // reasons; renaming the route is out of scope for this change.
    path: '/admin/transactions',
    icon: 'DocumentTextIcon',
    roles: ['CASE_MANAGER', 'COMPLIANCE', 'OPERATIONS', 'ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    id: 'admin-users',
    label: 'Users',
    path: '/admin/users',
    icon: 'UsersIcon',
    roles: ['ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    id: 'admin-discounts',
    label: 'Discount Codes',
    path: '/admin/discount-codes',
    icon: 'TagIcon',
    roles: ['ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    id: 'admin-transfer-agents',
    label: 'Transfer Agents',
    path: '/admin/transfer-agents',
    icon: 'BuildingOffice2Icon',
    roles: ['ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    id: 'admin-surveys',
    label: 'Surveys',
    path: '/admin/surveys',
    icon: 'ClipboardDocumentListIcon',
    roles: ['ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    id: 'admin-notifications',
    // OPS sees this as a read-only audit view. Edits/toggles are gated
    // to SUPER_ADMIN inside the page component itself.
    label: 'Notifications',
    path: '/admin/notifications',
    icon: 'BellIcon',
    roles: ['OPERATIONS', 'ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
  {
    id: 'admin-reports',
    label: 'Reports',
    path: '/admin/reports',
    icon: 'ChartBarIcon',
    roles: ['OPERATIONS', 'ADMIN', 'SUPER_ADMIN'],
    section: 'admin',
  },
];

/**
 * Filter navigation items by user role.
 * Returns ALL items the role can access.
 * @param role The current user's role
 * @returns Navigation items accessible to that role
 */
export function getNavigationForRole(role: UserRole): NavItem[] {
  return navigationItems.filter(item => item.roles.includes(role));
}
